/* 작가 HTML 화면(상태창·명령어) 틀 채우기 — 로판AI·위프처럼 작가가 화면 모양을 직접 짠다.
   문법은 두 가지뿐: {{이름}} 값 넣기, {{#항목}} … {{/항목}} 목록 반복(명령어 결과의 알림·글·메시지 한 줄씩).
   값은 전부 HTML 이스케이프한다 — 작가 틀은 믿되, AI가 만든 값이 태그로 끼어들지 못하게.
   그려진 문서는 스크립트 없는 샌드박스 iframe 에 들어간다(custom-screen.tsx). */

import type { ChatStatus } from '@/features/veluvit/lib/chat'
import type {
  CommandItem,
  StatusBoardField,
} from '@/features/veluvit/types/api.types'

type ScreenValues = Record<string, string | number | null | undefined>

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const fill = (template: string, values: ScreenValues) =>
  template.replace(/\{\{\s*([^{}#/\s][^{}]*?)\s*\}\}/g, (_, name: string) =>
    escapeHtml(values[name.trim()])
  )

/** 틀 + 값 → HTML. 목록 구간은 항목마다 반복하고, 항목 값이 바깥 값보다 앞선다 */
export function renderScreen(
  template: string,
  values: ScreenValues,
  lists: Record<string, ScreenValues[]> = {}
) {
  const looped = template.replace(
    /\{\{#\s*([^{}]+?)\s*\}\}([\s\S]*?)\{\{\/\s*\1\s*\}\}/g,
    (_, name: string, body: string) =>
      (lists[name.trim()] || [])
        .map((row) => fill(body, { ...values, ...row }))
        .join('')
  )
  return fill(looped, values)
}

/**
 * 서버 상태 객체에서 작가 상태창 값만 골라 낸다 — 서버(엔진 studio-renew.mjs statusValues)는 따로 statusValues 를 보내지 않고
 * 방 · 턴의 status 안에 항목 이름 그대로({"허기": 90, "관계": "경멸"}) 넣는다. 글자 · 숫자 값만 남긴다(2026-10-13).
 * 작품 initialStatus 도 같은 모양이라 시작값으로 쓴다(공개본 statusBoard.fields 에는 initial 이 없다)
 */
export function pickBoardValues(
  raw: unknown
): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  return Object.fromEntries(
    Object.entries(raw as Record<string, unknown>)
      .filter(
        ([, value]) =>
          typeof value === 'string' ||
          (typeof value === 'number' && Number.isFinite(value))
      )
      .map(([key, value]) => [key, String(value)])
  )
}

/**
 * 작가 상태창(statusBoard) 값 — 항목마다 시작값(initial) 위에 이번 턴 값(statusValues)을 덮는다. {{캐릭터}} 는 앱이 채운다.
 * 키는 작가가 정한 항목 이름 그대로({{호감도}} · {{의심도}} · {{체력}} …) — 스튜디오 미리보기(sampleValues)와 같은 규칙
 */
export function boardValues(
  fields: StatusBoardField[],
  values: Record<string, string> | undefined,
  extra: { name: string; workName?: string }
): ScreenValues {
  const visible = fields.filter((field) => !field.hidden)
  const base = Object.fromEntries(
    visible.map((field) => [field.key, values?.[field.key] ?? field.initial])
  )
  const aliases = Object.fromEntries(
    Object.entries(base).map(([key, value]) => [`status.${key}`, value])
  )
  return {
    캐릭터: extra.name,
    'work.name': extra.workName ?? '',
    ...aliases,
    ...base,
    ...stepNumbers(visible, base),
  }
}

/**
 * 선택형 항목의 지금 단계 번호 — {{관계.단계}}: 선택지 중 몇 번째인지(1부터, 없으면 0).
 * 서버는 상태창 HTML 을 정리하며 <style> 을 인라인으로 바꾸고 속성 선택자 · CSS 변수 · ::before 를 지운다(sai-backend authoring.py sanitize_screen).
 * 그래서 디자인 템플릿(status-themes.ts)은 단계 칸을 이 숫자로 인라인 계산해 칠한다. 스튜디오 미리보기도 같은 값을 쓴다
 */
export function stepNumbers(
  fields: { key: string; type: string; options?: string[] }[],
  values: Record<string, unknown>
): Record<string, string> {
  return Object.fromEntries(
    fields
      .filter((field) => field.type === 'list' && field.options?.length)
      .map((field) => [
        `${field.key}.단계`,
        String(
          (field.options ?? []).indexOf(String(values[field.key] ?? '')) + 1
        ),
      ])
  )
}

/** 상태창 틀에 넣을 값(옛 방식 screens.status) — 고정 항목 이름. 작가 안내(스튜디오)와 이름을 맞춘다 */
export function statusValues(
  status: ChatStatus,
  extra: { name: string; relation?: string; time?: string }
): ScreenValues {
  return {
    캐릭터: extra.name,
    날짜: status.date,
    시간: extra.time,
    장소: status.place,
    날씨: status.weather,
    표정: status.expression || status.expr,
    복장: status.outfit,
    내복장: status.userOutfit,
    속마음: status.inner,
    호감도: status.aff,
    관계: extra.relation,
  }
}

/** 명령어 결과 목록 — {{#항목}} 안에서 {{제목}} {{내용}} {{보낸사람}} {{시각}} */
export const commandRows = (items: CommandItem[]): ScreenValues[] =>
  items.map((x) => ({
    제목: x.title,
    내용: x.text,
    보낸사람: x.speaker,
    시각: x.when,
  }))

/** 스튜디오 안내용 — 틀에 쓸 수 있는 이름 */
export const STATUS_VARIABLES = [
  '캐릭터',
  '날짜',
  '시간',
  '장소',
  '날씨',
  '표정',
  '복장',
  '내복장',
  '속마음',
  '호감도',
  '관계',
]
export const COMMAND_VARIABLES = [
  '캐릭터',
  '화면이름',
  '#항목 … /항목',
  '제목',
  '내용',
  '보낸사람',
  '시각',
]
